import { useIntl } from 'react-intl';

import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { DropdownContent } from '@/patterns/global-nav/src/dropdown-content.tsx';
import { GlobalNav } from '@/patterns/global-nav/src/nav-global';
import { CompoundSubNavProps } from '@/patterns/global-nav/src/nav-global-compound-subnav';

export const CareerDetailsNavbar = () => {
	const intl = useIntl();

	// Title
	const careersHref = useLocalizedHref('/company/careers');

	// Top-level flat links
	const browseJobsHref = useLocalizedHref('/company/careers/all-jobs');
	const earlyCareersHref = useLocalizedHref('/company/careers/earlycareers');

	// Our Teams dropdown
	const engineeringHref = useLocalizedHref('/company/careers/engineering');
	const aiEngineeringHref = useLocalizedHref('/company/careers/engineering/ai');
	const salesHref = useLocalizedHref('/company/careers/sales');
	const allTeamsHref = useLocalizedHref('/company/careers/teams');

	// Team Experience dropdown
	const teamAnywhereHref = useLocalizedHref('/company/careers/teamanywhere');
	const teamEveryoneHref = useLocalizedHref('/company/careers/team-everyone');
	const employeeAwardsHref = useLocalizedHref('/company/careers/employee-awards');

	// Candidate Resources dropdown
	const candidateResourcesHubHref = useLocalizedHref('/company/careers/resources');
	const applyingHref = useLocalizedHref('/company/careers/resources/applying');
	const interviewingHref = useLocalizedHref('/company/careers/resources/interviewing');
	const careerGrowthHref = useLocalizedHref('/company/careers/resources/career-growth');
	const benefitsAndPerksHref = useLocalizedHref('/company/careers/resources/perk-and-benefits');
	const talentCommunityHref = useLocalizedHref(
		'https://join.atlassian.com/atlassian-talent-community/talentcommunity/form',
	);
	const awardsHref = useLocalizedHref('/company/careers/resources/awards');

	const props: CompoundSubNavProps = {
		compoundSubNavTitle: intl.formatMessage({
			id: 'CareerDetailsNavbar.title',
			defaultMessage: 'Careers',
		}),
		compoundSubNavTitleHref: careersHref,
		compoundSubNavLinksProps: [
			{
				globalLinksKey: 'browse-jobs',
				linkText: intl.formatMessage({
					id: 'CareerDetailsNavbar.browseJobs',
					defaultMessage: 'Browse Jobs',
				}),
				hasSubnav: false,
				href: browseJobsHref,
			},
			{
				globalLinksKey: 'our-teams',
				linkText: intl.formatMessage({
					id: 'CareerDetailsNavbar.ourTeams',
					defaultMessage: 'Our Teams',
				}),
				hasSubnav: true,
				content: (
					<DropdownContent
						orientation="column"
						contentSections={[
							{
								links: [
									{
										text: intl.formatMessage({
											id: 'CareerDetailsNavbar.engineering',
											defaultMessage: 'Engineering',
										}),
										href: engineeringHref,
									},
									{
										text: intl.formatMessage({
											id: 'CareerDetailsNavbar.aiAndMachineLearning',
											defaultMessage: 'AI and Machine Learning',
										}),
										href: aiEngineeringHref,
									},
									{
										text: intl.formatMessage({
											id: 'CareerDetailsNavbar.sales',
											defaultMessage: 'Sales',
										}),
										href: salesHref,
									},
									{
										text: intl.formatMessage({
											id: 'CareerDetailsNavbar.allTeams',
											defaultMessage: 'All Teams',
										}),
										href: allTeamsHref,
									},
								],
							},
						]}
					/>
				),
			},
			{
				globalLinksKey: 'early-careers',
				linkText: intl.formatMessage({
					id: 'CareerDetailsNavbar.earlyCareers',
					defaultMessage: 'Early Careers',
				}),
				hasSubnav: false,
				href: earlyCareersHref,
			},
			{
				globalLinksKey: 'team-experience',
				linkText: intl.formatMessage({
					id: 'CareerDetailsNavbar.teamExperience',
					defaultMessage: 'Team Experience',
				}),
				hasSubnav: true,
				content: (
					<DropdownContent
						orientation="column"
						contentSections={[
							{
								links: [
									{
										text: intl.formatMessage({
											id: 'CareerDetailsNavbar.teamAnywhere',
											defaultMessage: 'Team Anywhere',
										}),
										href: teamAnywhereHref,
									},
									{
										text: intl.formatMessage({
											id: 'CareerDetailsNavbar.teamEveryone',
											defaultMessage: 'Team Everyone',
										}),
										href: teamEveryoneHref,
									},
									{
										text: intl.formatMessage({
											id: 'CareerDetailsNavbar.employeeAwards',
											defaultMessage: 'Employee Awards',
										}),
										href: employeeAwardsHref,
									},
								],
							},
						]}
					/>
				),
			},
			{
				globalLinksKey: 'candidate-resources',
				linkText: intl.formatMessage({
					id: 'CareerDetailsNavbar.candidateResources',
					defaultMessage: 'Candidate Resources',
				}),
				hasSubnav: true,
				content: (
					<DropdownContent
						orientation="column"
						contentSections={[
							{
								links: [
									{
										text: intl.formatMessage({
											id: 'CareerDetailsNavbar.candidateResourcesHub',
											defaultMessage: 'Candidate Resources Hub',
										}),
										href: candidateResourcesHubHref,
									},
									{
										text: intl.formatMessage({
											id: 'CareerDetailsNavbar.tipsForApplying',
											defaultMessage: 'Tips for Applying',
										}),
										href: applyingHref,
									},
									{
										text: intl.formatMessage({
											id: 'CareerDetailsNavbar.interviewing',
											defaultMessage: 'Interviewing',
										}),
										href: interviewingHref,
									},
									{
										text: intl.formatMessage({
											id: 'CareerDetailsNavbar.careerGrowth',
											defaultMessage: 'Career Growth',
										}),
										href: careerGrowthHref,
									},
									{
										text: intl.formatMessage({
											id: 'CareerDetailsNavbar.benefitsAndPerks',
											defaultMessage: 'Benefits and Perks',
										}),
										href: benefitsAndPerksHref,
									},
									{
										text: intl.formatMessage({
											id: 'CareerDetailsNavbar.talentCommunity',
											defaultMessage: 'Talent Community',
										}),
										href: talentCommunityHref,
									},
									{
										text: intl.formatMessage({
											id: 'CareerDetailsNavbar.awards',
											defaultMessage: 'Awards',
										}),
										href: awardsHref,
									},
								],
							},
						]}
					/>
				),
			},
		],
		isShowCta: false,
	};

	return <GlobalNav productKey={'home'} compoundNav={props} />;
};
